import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';

import {
  ANNOTATION_SOURCE_LOCATION,
  isGroupEntity,
} from '@backstage/catalog-model';
import {
  ApiBlueprint,
  configApiRef,
  createFrontendPlugin,
  fetchApiRef,
  microsoftAuthApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { EntityTagFilter } from '@backstage/plugin-catalog-react';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import { Icon } from '@iconify/react';
import {
  isApiEntity,
  isChapterEntity,
  isComponentEntity,
  isDataProductEntity,
  isDemandEntity,
  isDomainEntity,
  isOrganisationDsoEntity,
  isPartnerSystemEntity,
  isProductEntity,
  isResourceEntity,
  isServiceEntity,
  isSolutionEntity,
  isSquadEntity,
  isSystemEntity,
  isTribeEntity,
} from '@ipen-portal/ipen-entity-model';
import {
  and,
  isIPenPlatformDomainEntity,
  not,
  or,
  showMonitoring,
  versionResponse,
} from '@ipen-portal/plugin-common';
import {
  DynamicDocsNavigationSubPathBlueprint,
  EnhancedNavItemBlueprint,
  HeaderComponentBlueprint,
  versionApiRef,
} from '@ipen-portal/plugin-common-react';
import { makeEntityContentWithToolbar } from '@ipen-portal/plugin-layout-react';
import { TrpcApiBlueprint } from '@ipen-portal/plugin-trpc-react';
import { Grid } from '@mui/material';
import { Navigate } from 'react-router';

import { AppSwitcherApi, appSwitcherApiRef } from './api';
import { azureStorageApiRef } from './apis';
import { breadcrumbDefinitions } from './breadcrumbs/breadcrumbDefinitions';
import { KeyRolesCard } from './components/cards/KeyRolesCard';
import {
  OrganisationTeamInformationCard,
  TeamInformationCardLeft,
  TeamInformationCardRight,
} from './components/cards/TeamInformationCard';
import { ApiListPageEntityContent } from './presentation/ApiListPage/ApiListPage';
import { ChapterListEntityContent } from './presentation/ChapterListPage/ChapterListPage';
import { DataProductListEntityContent } from './presentation/DataProductListPage/DataProductListPage';
import { DataProductEntityContent } from './presentation/DataProductPage/DataProductPage';
import { PartnerSystemsEntityContent } from './presentation/PartnerSystemListPage/PartnerSystemListPage';
import { ProductListEntityContent } from './presentation/ProductListPage/ProductListPage';
import { SquadListEntityContent } from './presentation/SquadListPage/SquadListPage';
import {
  catalogEntityRouteRef,
  catalogIndexRouteRef,
  createComponentRouteRef,
  registerComponentRouteRef,
  rootRoute,
  userSettingsRouteRef,
} from './routes';
import { validateFileAttachments } from './scaffolder-extensions/FileAttachment/validate';

const apis = [
  TrpcApiBlueprint.make({
    name: 'azure-storage-api',
    params: defineParams =>
      defineParams({
        apiRef: azureStorageApiRef,
        path: 'azure-storage',
      }),
  }),
  ApiBlueprint.make({
    name: 'app-switcher',
    params: defineParams =>
      defineParams({
        api: appSwitcherApiRef,
        deps: {
          config: configApiRef,
          microsoftAuthApi: microsoftAuthApiRef,
        },
        factory: deps => new AppSwitcherApi(deps),
      }),
  }),
  ApiBlueprint.make({
    name: 'version',
    params: defineParams =>
      defineParams({
        api: versionApiRef,
        deps: {
          config: configApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ config, fetchApi }) => {
          const baseUrl = config.getString('backend.baseUrl');
          return {
            getVersion: async () => {
              const response = await fetchApi.fetch(`${baseUrl}/version`);
              const contentType = response.headers.get('content-type') ?? '';
              const payload = contentType.includes('application/json')
                ? await response.json()
                : await response.text();

              return versionResponse.parse(payload);
            },
          };
        },
      }),
  }),
];

const OrganisationListPage = PageBlueprint.make({
  name: 'OrganisationListPage',
  params: {
    loader: () =>
      import('./presentation/OrganisationListPage/OrganisationListPage').then(
        m => <m.OrganisationListPage />,
      ),
    noHeader: true,
    path: '/organisations',
    routeRef: rootRoute,
  },
});

const DsoListPage = PageBlueprint.make({
  name: 'DsoListPage',
  params: {
    loader: () =>
      import('./presentation/DsoListPage/DsoListPage').then(m => (
        <m.DsoListPage />
      )),
    noHeader: true,
    path: '/organisation-dsos',
    routeRef: rootRoute,
  },
});

const MarketplacePage = PageBlueprint.make({
  name: 'MarketplacePage',
  params: {
    loader: () =>
      import('./presentation/SolutionListPage/SolutionListPage').then(m => (
        <m.MarketplacePage />
      )),
    noHeader: true,
    path: '/marketplace',
    routeRef: rootRoute,
  },
});

const OwnershipCard = EntityCardBlueprint.make({
  name: 'ownership',
  params: {
    filter: isGroupEntity,
    loader: async () =>
      import('./components/cards/OwnershipCard').then(m => <m.OwnershipCard />),
  },
});

const MembersCard = EntityCardBlueprint.make({
  name: 'members',
  params: {
    filter: and(isGroupEntity, not(or(isOrganisationDsoEntity, isTribeEntity))),
    loader: async () =>
      import('./components/cards/MembersCard').then(m => <m.MembersCard />),
    type: 'info',
  },
});

const InfrastructureSuiteListEntityContent = makeEntityContentWithToolbar({
  name: 'InfrastructureSuiteListEntityContent',
  params: {
    filter: isIPenPlatformDomainEntity,
    icon: <Icon icon="tabler:topology-complex" />,
    loader: async () =>
      import('./presentation/InfrastructureSuiteListPage/components/InfrastructureSuiteList').then(
        m => <m.InfrastructureSuiteList />,
      ),
    path: '/infrastructure-suite',
    title: 'Infrastructure Suite',

    wrapperProps: {
      kindFilter: 'template',
      staticFilter: {
        filterTag: new EntityTagFilter(['infrastructure']),
      },
    },
  },
});

const IPenEntityPickerFieldExtension = FormFieldBlueprint.make({
  name: 'IPenEntityPicker',
  params: {
    field: () =>
      import('./scaffolder-extensions/IPenEntityPicker').then(m =>
        createFormField({
          component: m.IPenEntityPicker,
          name: 'IPenEntityPicker',
          schema: m.IPenEntityPickerFieldSchema,
        }),
      ),
  },
});

const FileAttachmentFieldExtension = FormFieldBlueprint.make({
  name: 'FileAttachment',
  params: {
    field: () =>
      import('./scaffolder-extensions/FileAttachment').then(m =>
        createFormField({
          component: m.FileAttachment,
          name: 'FileAttachment',
          schema: m.FileAttachmentFieldSchema,
          validation: validateFileAttachments,
        }),
      ),
  },
});

const TenantInformationCard = EntityCardBlueprint.make({
  name: 'tenant-information',
  params: {
    filter: isOrganisationDsoEntity,
    loader: async () =>
      import('./components/cards/TenantInformationCard').then(m => (
        <m.TenantInformationCard />
      )),
  },
});

const DemandBusinessImpactCard = EntityCardBlueprint.make({
  name: 'demand-business-impact',
  params: {
    filter: isDemandEntity,
    loader: async () =>
      import('./components/cards/DemandBusinessImpactCard').then(m => (
        <m.DemandBusinessImpactCard />
      )),
  },
});

const DemandKeyRolesCard = EntityCardBlueprint.make({
  name: 'demand-key-roles',
  params: {
    filter: isDemandEntity,
    loader: async () =>
      import('./components/cards/DemandKeyRolesCard').then(m => (
        <m.DemandKeyRolesCard />
      )),
    type: 'content',
  },
});

const DemandStatusCard = EntityCardBlueprint.make({
  name: 'demand-status',
  params: {
    filter: isDemandEntity,
    loader: async () =>
      import('./components/cards/DemandStatusCard').then(m => (
        <m.DemandStatusCard />
      )),
    type: 'info',
  },
});

const DynamicDocsSubPathOfDemand = DynamicDocsNavigationSubPathBlueprint.make({
  name: 'DynamicDocsSubPathOfDemand',
  params: defineParams =>
    defineParams({
      filter: isDemandEntity,
      subPath: e =>
        e.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION]?.replace(
          /^.*\/blob\/[^/]+\/docs\/(.*?)(?:\?.*)?$/,
          '$1',
        ),
    }),
});

const ServicePrincipalCard = EntityCardBlueprint.make({
  name: 'service-principal',
  params: {
    filter: or(isComponentEntity, isOrganisationDsoEntity, isSolutionEntity),
    loader: async () =>
      import('./components/cards/ServicePrincipalCard').then(m => (
        <m.ServicePrincipalCard />
      )),
  },
});

const AdGroupCard = EntityCardBlueprint.make({
  name: 'ad-group',
  params: {
    filter: or(isComponentEntity, isSolutionEntity),
    loader: async () =>
      import('./components/cards/AdGroupCard').then(m => <m.AdGroupCard />),
  },
});

const ApisOfSystemAndDataProductCard = EntityCardBlueprint.make({
  name: 'apis-of-system-and-data-product',
  params: {
    filter: or(isProductEntity, isPartnerSystemEntity, isDataProductEntity),
    loader: async () =>
      import('./components/cards/ServiceCard').then(m => (
        <m.ServiceCard
          creationTemplateRef="ipen-api-spec"
          icon="ant-design:api-outlined"
          predicate={isApiEntity}
          serviceKind="API"
          title="APIs"
        />
      )),
  },
});

const ComponentsOfSystemAndDataProductCard = EntityCardBlueprint.make({
  name: 'components-of-system-and-data-product',
  params: {
    filter: or(isProductEntity, isPartnerSystemEntity, isDataProductEntity),
    loader: async () =>
      import('./components/cards/ServiceCard').then(m => (
        <m.ServiceCard
          icon="material-symbols:category"
          predicate={isComponentEntity}
          serviceKind="Component"
          title="Components"
        />
      )),
  },
});

const ResourcesOfSystemAndDataProductCard = EntityCardBlueprint.make({
  name: 'resources-of-system-and-data-product',
  params: {
    filter: or(isProductEntity, isPartnerSystemEntity, isDataProductEntity),
    loader: async () =>
      import('./components/cards/ServiceCard').then(m => (
        <m.ServiceCard
          icon="ic:outline-home-repair-service"
          predicate={isResourceEntity}
          serviceKind="Resource"
          title="Resources"
        />
      )),
  },
});

const DescriptionCard = EntityCardBlueprint.make({
  name: 'description',
  params: {
    filter: or(
      isApiEntity,
      isComponentEntity,
      isDomainEntity,
      and(isGroupEntity, not(isOrganisationDsoEntity)),
      isResourceEntity,
      and(isSystemEntity, not(or(isSolutionEntity, isProductEntity))),
    ),
    loader: async () =>
      import('./components/cards/DescriptionCard').then(m => (
        <m.DescriptionCard />
      )),
    type: 'info',
  },
});

const LifecycleCard = EntityCardBlueprint.make({
  name: 'lifecycle',
  params: {
    filter: or(isApiEntity, isComponentEntity, isSystemEntity),
    loader: async () =>
      import('./components/cards/LifecycleCard').then(m => <m.LifecycleCard />),
    type: 'info',
  },
});

const SolutionModulesCard = EntityCardBlueprint.make({
  name: 'solution-modules',
  params: {
    filter: isSolutionEntity,
    loader: async () =>
      import('./components/cards/SolutionModulesCard').then(m => (
        <m.SolutionModulesCard />
      )),
    type: 'info',
  },
});

const ContractsConsumedBySystemCard = EntityCardBlueprint.make({
  name: 'contracts-consumed-by-system',
  params: {
    filter: or(isSystemEntity, isDataProductEntity),
    loader: async () =>
      import('./components/cards/ContractsConsumedBySystemCard').then(m => (
        <m.ContractsConsumedBySystemCard />
      )),
    type: 'content',
  },
});

const ContractsProvidedBySystemCard = EntityCardBlueprint.make({
  name: 'contracts-provided-by-system',
  params: {
    filter: or(isSystemEntity, isDataProductEntity),
    loader: async () =>
      import('./components/cards/ContractsProvidedBySystemCard/ContractsProvidedBySystemCard').then(
        m => <m.ContractsProvidedBySystemCard />,
      ),
    type: 'content',
  },
});

const ContractsProvidedByApiOrComponentCard = EntityCardBlueprint.make({
  name: 'contracts-provided-by-api-or-component',
  params: {
    filter: or(isComponentEntity, isApiEntity),
    loader: async () =>
      import('./components/cards/ContractsProvidedByApiOrComponentCard').then(
        m => <m.ContractsProvidedByApiOrComponentCard />,
      ),
    type: 'content',
  },
});

const BrandSpacePage = EntityContentBlueprint.make({
  name: 'BrandSpacePage',
  params: {
    filter: isIPenPlatformDomainEntity,
    icon: <Icon icon="ion:leaf-sharp" />,
    loader: async () => (
      <Navigate replace to="/catalog/default/component/ipen-theme/docs/" />
    ),
    path: '/brand-space',
    title: 'Brand Space',
  },
});

const ApisOfComponentsPage = EntityContentBlueprint.make({
  name: 'ApisOfComponentsPage',
  params: {
    filter: and(isServiceEntity, showMonitoring),
    icon: <Icon icon="carbon:api-1" />,
    loader: async () => {
      const { EntityConsumedApisCard, EntityProvidedApisCard } =
        await import('@backstage/plugin-api-docs');
      return (
        <Grid alignItems="stretch" container spacing={3}>
          <Grid item md={6}>
            <EntityProvidedApisCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumedApisCard />
          </Grid>
        </Grid>
      );
    },
    path: '/api',

    title: 'API',
  },
});

const headerButtons = [
  HeaderComponentBlueprint.make({
    name: 'Search',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => <m.HeaderSearch />),
    },
  }),
  HeaderComponentBlueprint.make({
    name: 'IncidentRequestButton',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => (
          <m.IncidentRequestButton />
        )),
    },
  }),
  HeaderComponentBlueprint.make({
    name: 'DemandRequestButton',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => (
          <m.DemandRequestButton />
        )),
    },
  }),
  HeaderComponentBlueprint.make({
    name: 'RatingButton',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => <m.RatingButton />),
    },
  }),
  HeaderComponentBlueprint.make({
    name: 'HelpButton',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => <m.HelpButton />),
    },
  }),
  HeaderComponentBlueprint.make({
    name: 'AppSwitcherButton',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => (
          <m.AppSwitcherButton />
        )),
    },
  }),
  HeaderComponentBlueprint.make({
    name: 'AccountButton',
    params: {
      loader: async () =>
        import('./components/EonHeaderButtons').then(m => <m.AccountButton />),
    },
  }),
];

export const ipenPlugin = createFrontendPlugin({
  extensions: [
    ...apis,
    MembersCard,
    LifecycleCard,
    SolutionModulesCard,
    DescriptionCard,
    TeamInformationCardLeft,
    TeamInformationCardRight,
    OrganisationTeamInformationCard,
    TenantInformationCard,
    ServicePrincipalCard,
    OwnershipCard,
    KeyRolesCard,
    AdGroupCard,
    ResourcesOfSystemAndDataProductCard,
    ComponentsOfSystemAndDataProductCard,
    ApisOfSystemAndDataProductCard,
    ContractsProvidedByApiOrComponentCard,
    ContractsProvidedBySystemCard,
    ContractsConsumedBySystemCard,
    DemandBusinessImpactCard,
    DemandKeyRolesCard,
    DemandStatusCard,
    DynamicDocsSubPathOfDemand,

    ...ApiListPageEntityContent,
    ...ChapterListEntityContent,
    DsoListPage,
    OrganisationListPage,
    ...PartnerSystemsEntityContent,
    ...SquadListEntityContent,
    MarketplacePage,
    ...InfrastructureSuiteListEntityContent,
    ...ProductListEntityContent,
    ...DataProductListEntityContent,
    DataProductEntityContent,
    IPenEntityPickerFieldExtension,
    FileAttachmentFieldExtension,
    BrandSpacePage,
    ApisOfComponentsPage,
    ...headerButtons,

    EnhancedNavItemBlueprint.make({
      name: 'devtools',
      params: {
        featureFlag: 'dev-nav',
        icon: <Icon icon="mdi:wrench" />,
        target: 'devtools',
        title: 'DevTools',
      },
    }),
    EnhancedNavItemBlueprint.make({
      name: 'marketplace',
      params: {
        entityMatcher: isSolutionEntity,
        icon: 'https://theme.ipen.eon.com/icons/navbar/solution.svg',
        pageTheme: 'yellow',
        target: '/marketplace',
        title: 'Marketplace',
      },
    }),
    EnhancedNavItemBlueprint.make({
      name: 'ipen-domain',
      params: {
        fallbackIcon: 'https://theme.ipen.eon.com/icons/navbar/platform_boxed.svg',
        filter: { 'kind': 'domain', 'metadata.name': 'ipen-domain' },
        pageTheme: 'turquoise',
      },
    }),
    EnhancedNavItemBlueprint.make({
      name: 'dsos',
      params: {
        description: 'Distributed Service Operators',
        entityMatcher: isOrganisationDsoEntity,
        icon: 'https://theme.ipen.eon.com/icons/navbar/organisation_boxed.svg',
        pageTheme: 'red',
        target: '/organisation-dsos',
        title: 'DSOs',
      },
    }),
    EnhancedNavItemBlueprint.make({
      name: 'tribe',
      params: {
        entityMatcher: e =>
          isTribeEntity(e) || isChapterEntity(e) || isSquadEntity(e),
        icon: 'https://theme.ipen.eon.com/icons/navbar/tribe_boxed.svg',
        pageTheme: 'purple',
        target: '/catalog/default/group/ipen-platform-tribe',
        title: 'Tribe',
      },
    }),
    ...breadcrumbDefinitions,
  ] as ExtensionDefinition[],
  externalRoutes: {
    catalogIndex: catalogIndexRouteRef,
    createComponent: createComponentRouteRef,
    registerApi: registerComponentRouteRef,
    userSettings: userSettingsRouteRef,
  },
  pluginId: 'ipen',
  routes: {
    catalogEntity: catalogEntityRouteRef,
    root: rootRoute,
  },
});
